import { Alert, BrandLogo, Button, Card, Field } from '@catering/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type JSX, useState } from 'react';

import { ApiError, apiRequest } from '../api/client';

/**
 * New for the portal — `apps/web-admin` has no equivalent screen: staff
 * invitations there are accepted through the same generic flow, but the
 * back-office never linked a UI to it. `client-portal/design.md` §6.3
 * asks to "copy" `AcceptInvitationPage`; there is nothing to copy, so
 * this is built directly against `POST /auth/invitations/accept`
 * (`portal-access/spec.md`).
 *
 * `AcceptInvitationUseCase`'s own exception filter maps every token
 * failure — expired, already accepted, unknown — to the same 401
 * (`auth-exception.filter.ts`'s own comment: every credential failure
 * "leaves through the same 401 with the same body"). This screen cannot
 * show a distinct "expirada" message the backend does not distinguish;
 * it shows one honest message for the whole class instead.
 */
export function AcceptInvitationPage(): JSX.Element {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/accept-invitation' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (token === undefined) {
      return;
    }

    const form = new FormData(event.currentTarget);
    const password = readText(form, 'password');

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await apiRequest('/auth/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });

      await navigate({ to: '/login' });
    } catch (error) {
      if (!(error instanceof ApiError)) {
        throw error;
      }

      setErrorMessage(describeAcceptError(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Portal de pedidos</span>
        </div>

        <Card variant="elevated" className="w-full">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
            noValidate
          >
            <header>
              <h1 className="text-lg font-semibold text-slate-900">Crear tu cuenta</h1>
              <p className="mt-1 text-sm text-slate-500">
                Elegí una contraseña para activar tu acceso al portal.
              </p>
            </header>

            {token === undefined ? (
              <Alert tone="danger">
                Este enlace no incluye una invitación válida. Pedile a quien te invitó que te envíe
                una nueva.
              </Alert>
            ) : null}

            {errorMessage !== null ? <Alert tone="danger">{errorMessage}</Alert> : null}

            <Field
              label="Contraseña"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              autoFocus
              disabled={token === undefined}
            />

            <Button
              type="submit"
              loading={isSubmitting}
              disabled={token === undefined}
              className="w-full"
            >
              Crear cuenta
            </Button>
          </form>
        </Card>
      </div>
    </main>
  );
}

function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}

const INVALID_INVITATION =
  'Invitación inválida o vencida. Pedile a quien te invitó que te envíe una nueva.';

/**
 * A rejected password is not a broken invitation: saying so would send the
 * person to ask for a new link when all they need is another password.
 */
function describeAcceptError(error: ApiError): string {
  const body = error.body as { violations?: unknown } | null;
  const violations = Array.isArray(body?.violations) ? (body.violations as string[]) : [];

  if (violations.includes('BREACHED')) {
    return 'Esa contraseña apareció en filtraciones de datos conocidas. Elegí otra.';
  }
  if (violations.includes('TOO_SHORT')) {
    return 'La contraseña tiene que tener al menos 10 caracteres.';
  }
  if (violations.includes('TOO_LONG')) {
    return 'La contraseña puede tener hasta 128 caracteres.';
  }

  return INVALID_INVITATION;
}
